import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  TextField, Select, MenuItem, FormControl, InputLabel, Typography,
  Box, Button, Checkbox, FormControlLabel, Link, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Tooltip
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Users, Check, HelpCircle, Info, ChevronRight, ChevronLeft, Edit, Smartphone, Calendar, Shield, CreditCard, Clock, AlertTriangle } from 'lucide-react';
import { useSpring, animated } from 'react-spring';
import './PostCircle.css';

// Import services
import { carrierService, circleService } from '../../services';
import { trackHostStarted } from '../../utils/analytics';

// Constants
const customerDurations = ['0-3 months', '3-6 months', '6-12 months', '1-2 years', '2+ years'];
const CACHE_EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_KEYS = {
  CARRIERS: 'circledin_carriers_data',
  PLANS: 'circledin_plans_cache',
};

// Helper Functions
const calculateAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/\D/g, '');

  if (phoneNumber.length >= 10) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  } else if (phoneNumber.length > 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  } else if (phoneNumber.length > 3) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return '';
};

const formatDateForBackend = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const isValidDate = (dateString, type = 'general') => {
  const today = new Date();
  const date = new Date(dateString);
  const minYear = 1900;

  if (type === 'birth_date') {
    const age = calculateAge(dateString);
    return (
      date instanceof Date &&
      !isNaN(date) &&
      date <= today &&
      date.getFullYear() >= minYear &&
      age >= 18
    );
  }

  return (
    date instanceof Date &&
    !isNaN(date) &&
    date <= today &&
    date.getFullYear() >= minYear
  );
};

// Check if cached data is still fresh
const isCacheFresh = (timestamp) => {
  const now = new Date().getTime();
  return now - timestamp < CACHE_EXPIRATION_TIME;
};

// Simple function to scroll to top - not dependent on any React hooks
function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}

// Components
const ErrorMessage = ({ message }) => (
  <div className="error-message">
    <AlertTriangle size={14} />
    <span>{message}</span>
  </div>
);

const SkeletonDropdown = ({ height = 56 }) => (
  <div className="skeleton-wrapper" style={{ height: `${height}px` }}>
    <div className="skeleton-shine"></div>
    <style jsx>{`
      .skeleton-wrapper {
        width: 100%;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.1);
        position: relative;
        overflow: hidden;
        margin-bottom: 0;
      }
      .skeleton-shine {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.1) 50%,
          rgba(255, 255, 255, 0) 100%
        );
        animation: shimmer 1.5s infinite;
      }
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `}</style>
  </div>
);

const ConfettiEffect = () => (
  <div className="confetti-container">
    {[...Array(50)].map((_, i) => (
      <div key={i} className="confetti" style={{
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 3}s`,
        backgroundColor: ['#ff4775', '#00d1e0', '#7ffaff', '#ff9800'][Math.floor(Math.random() * 4)]
      }}></div>
    ))}
  </div>
);

const BenefitItem = ({ icon: Icon, text }) => (
  <div className="benefit-item">
    <motion.div
      className="benefit-icon"
      whileHover={{ scale: 1.1, rotate: 5 }}
    >
      <Icon size={20} />
    </motion.div>
    <span>{text}</span>
  </div>
);

const ConfirmCircle = ({ formData, onEdit }) => {
  return (
    <div className="modern-confirm-card">
      <ConfettiEffect />
      <div className="circle-avatar">
        <Users size={60} color="#ffffff" />
      </div>
      <h2 className="circle-name">{formData.circleName} is Ready!</h2>
      <div className="circle-info">
        <span className="info-chip">
          <Smartphone size={16} /> {formData.carrier}
        </span>
        <span className="info-chip">
          <Users size={16} /> {formData.familySize} Members
        </span>
        <span className="info-chip">
          <Calendar size={16} /> {formData.customerDuration}
        </span>
      </div>
      <p className="confirmation-text">
        Your circle is set up with {formData.carrier} on the {formData.planName} plan.
        Your primary number is {formData.primaryNumber}.
      </p>

      <div className="next-steps">
        <h3>What's Next?</h3>
        <ul>
          <li><Check size={16} /> We'll process your circle creation</li>
          <li><Check size={16} /> You'll receive a confirmation email within 24 hours</li>
          <li><Check size={16} /> Start inviting members to join your circle!</li>
        </ul>
      </div>

      <motion.button
        className="edit-button"
        onClick={onEdit}
        whileHover={{
          scale: 1.05,
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)"
        }}
        whileTap={{ scale: 0.98 }}
      >
        <ChevronLeft size={16} />
        Edit Details
      </motion.button>
    </div>
  );
};

const PostCircle = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    carrier: '',
    planName: '',
    customerDuration: '',
    primaryNumber: '',
    circleName: '',
    familySize: '',
    monthly_payment_date: '',
    monthly_payment_per_line: '',
    date_of_birth: ''
  });

  // UI reference state
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Backend data states
  const [carriers, setCarriers] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(true);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [error, setError] = useState(null);
  const [carrierCache, setCarrierCache] = useState({});

  // UI states
  const [agreeCircledin, setAgreeCircledin] = useState(false);
  const [agreeCarrier, setAgreeCarrier] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // host_started should fire once, at the start of the hosting flow
  const hostStartedFiredRef = useRef(false);

  // host_started: the visitor has started the hosting flow. Fires once on
  // mount; carrier isn't picked yet at this point, so it's optional.
  useEffect(() => {
    if (!hostStartedFiredRef.current) {
      hostStartedFiredRef.current = true;
      trackHostStarted();
    }
  }, []);

  // Mouse move handler - simple function, no dependencies
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: (e.clientX - rect.left) / rect.width - 0.5,
      y: (e.clientY - rect.top) / rect.height - 0.5
    });
  };

  // Add error styles to document
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .error-banner {
        display: flex;
        align-items: center;
        gap: 10px;
        background-color: rgba(255, 59, 48, 0.15);
        color: #FF3B30;
        padding: 12px 16px;
        border-radius: 12px;
        margin-bottom: 20px;
        border: 1px solid rgba(255, 59, 48, 0.3);
        animation: pulse 2s infinite;
      }
      
      @keyframes pulse {
        0% { box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.4); }
        70% { box-shadow: 0 0 0 6px rgba(255, 59, 48, 0); }
        100% { box-shadow: 0 0 0 0 rgba(255, 59, 48, 0); }
      }
      
      .error-message {
        color: #FF3B30;
        font-size: 0.85rem;
        margin-top: 6px;
        display: flex;
        align-items: center;
        gap: 6px;
      }
      
      .custom-select-wrapper, .custom-input-wrapper {
        position: relative;
      }
      
      .Mui-error .MuiOutlinedInput-notchedOutline {
        border-color: #FF3B30 !important;
        border-width: 2px !important;
      }
      
      .error-checkbox {
        border-color: #FF3B30 !important;
        border-width: 2px !important;
        animation: errorPulse 2s infinite;
      }
      
      @keyframes errorPulse {
        0% { box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.7); }
        70% { box-shadow: 0 0 0 4px rgba(255, 59, 48, 0); }
        100% { box-shadow: 0 0 0 0 rgba(255, 59, 48, 0); }
      }
      
      /* Highlight date picker fields with errors */
      input[type="date"].Mui-error {
        border-color: #FF3B30;
        border-width: 2px;
        background-color: rgba(255, 59, 48, 0.05);
      }
      
      /* Make error messages more prominent */
      .global-error {
        font-weight: 500;
        padding: 8px 12px;
        border-radius: 8px;
        background-color: rgba(255, 59, 48, 0.08);
        margin-bottom: 16px;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    scrollToTop();
  }, []);

  // Load cached data
  useEffect(() => {
    const loadCachedData = () => {
      try {
        // Load carriers with expiration check
        const cachedCarrierData = localStorage.getItem(CACHE_KEYS.CARRIERS);
        if (cachedCarrierData) {
          const { data, timestamp } = JSON.parse(cachedCarrierData);
          
          if (isCacheFresh(timestamp) && Array.isArray(data) && data.length > 0) {
            setCarriers(data);
            setIsLoadingCarriers(false);
          }
        }
        
        // Load carrier plans cache with expiration check
        const cachedPlansData = localStorage.getItem(CACHE_KEYS.PLANS);
        if (cachedPlansData) {
          const { data, timestamp } = JSON.parse(cachedPlansData);
          
          if (isCacheFresh(timestamp) && typeof data === 'object') {
            setCarrierCache(data);
          }
        }
      } catch (error) {
        console.error('Error loading cached data:', error);
      }
    };
    
    loadCachedData();
  }, []);

  // Fetch carriers
  useEffect(() => {
    const fetchCarriers = async () => {
      if (carriers.length > 0 && !isLoadingCarriers) {
        return;
      }
      
      try {
        const token = await getAccessTokenSilently();
        setError(null);
        
        const response = await carrierService.getAllCarriers(token);
        const carriersData = response.data || [];
        
        if (Array.isArray(carriersData)) {
          setCarriers(carriersData);
          
          localStorage.setItem(CACHE_KEYS.CARRIERS, JSON.stringify({
            data: carriersData,
            timestamp: new Date().getTime()
          }));
        } else {
          setError('Received invalid carrier data format');
        }
      } catch (error) {
        console.error('Error fetching carriers:', error);
        setError(error.friendlyMessage || 'Failed to load carriers. Please try again later.');
      } finally {
        setIsLoadingCarriers(false);
      }
    };

    fetchCarriers();
  }, [getAccessTokenSilently, carriers.length, isLoadingCarriers]);

  // Fetch plans when carrier changes
  useEffect(() => {
    const fetchPlans = async () => {
      if (!formData.carrier) {
        setAvailablePlans([]);
        return;
      }

      // Check if we already have this carrier's plans in cache
      if (carrierCache[formData.carrier]) {
        // Important: create a new array to force state update
        setAvailablePlans([...carrierCache[formData.carrier]]);
        setIsLoadingPlans(false);
        return;
      }

      setIsLoadingPlans(true);
      try {
        const token = await getAccessTokenSilently();
        setError(null);
        
        const response = await carrierService.getCarrierPlans(formData.carrier, token);
        
        // Extract plans from response
        let plans = [];
        
        if (response.data && Array.isArray(response.data.plans)) {
          plans = response.data.plans;
        } else if (response.data && Array.isArray(response.data)) {
          plans = response.data;
        } else {
          // Try to find a plans property at any level
          const findPlans = (obj) => {
            if (!obj || typeof obj !== 'object') return null;
            if (Array.isArray(obj.plans)) return obj.plans;
            
            for (const key in obj) {
              if (key === 'plans' && Array.isArray(obj[key])) {
                return obj[key];
              }
              if (typeof obj[key] === 'object') {
                const result = findPlans(obj[key]);
                if (result) return result;
              }
            }
            return null;
          };
          
          const foundPlans = findPlans(response);
          if (foundPlans) {
            plans = foundPlans;
          }
        }
        
        // Update cache with new data
        const updatedCache = {
          ...carrierCache,
          [formData.carrier]: plans
        };
        
        setCarrierCache(updatedCache);
        setAvailablePlans([...plans]);
        
        localStorage.setItem(CACHE_KEYS.PLANS, JSON.stringify({
          data: updatedCache,
          timestamp: new Date().getTime()
        }));
      } catch (error) {
        console.error('Error fetching plans:', error);
        setError(error.friendlyMessage || 'Failed to load plans. Please try again later.');
        setAvailablePlans([]);
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, [formData.carrier, getAccessTokenSilently, carrierCache]);

  // Prefetch all carrier plans
  useEffect(() => {
    const prefetchAllPlans = async () => {
      if (carriers.length === 0 || isLoadingCarriers) return;
      
      const prefetchPromises = carriers.map(async (carrier) => {
        if (carrierCache[carrier.slug]) return null;

        try {
          const token = await getAccessTokenSilently();
          
          const response = await carrierService.getCarrierPlans(carrier.slug, token);
          
          // Extract plans
          let plans = [];
          
          if (response.data && Array.isArray(response.data.plans)) {
            plans = response.data.plans;
          } else if (response.data && Array.isArray(response.data)) {
            plans = response.data;
          } else {
            // Try to find plans property
            const findPlans = (obj) => {
              if (!obj || typeof obj !== 'object') return null;
              if (Array.isArray(obj.plans)) return obj.plans;
              
              for (const key in obj) {
                if (key === 'plans' && Array.isArray(obj[key])) {
                  return obj[key];
                }
                if (typeof obj[key] === 'object') {
                  const result = findPlans(obj[key]);
                  if (result) return result;
                }
              }
              return null;
            };
            
            const foundPlans = findPlans(response);
            if (foundPlans) {
              plans = foundPlans;
            }
          }
          
          return {
            carrierSlug: carrier.slug,
            plans: plans
          };
        } catch (error) {
          console.error(`Error prefetching plans for ${carrier.name}:`, error);
          return {
            carrierSlug: carrier.slug,
            plans: []
          };
        }
      });
      
      const results = await Promise.allSettled(prefetchPromises);
      const newCacheEntries = {};
      
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          const { carrierSlug, plans } = result.value;
          newCacheEntries[carrierSlug] = plans;
        }
      });
      
      if (Object.keys(newCacheEntries).length > 0) {
        const updatedCache = { ...carrierCache, ...newCacheEntries };
        setCarrierCache(updatedCache);
        
        localStorage.setItem(CACHE_KEYS.PLANS, JSON.stringify({
          data: updatedCache,
          timestamp: new Date().getTime()
        }));
      }
    };
    
    prefetchAllPlans();
  }, [carriers, isLoadingCarriers, getAccessTokenSilently, carrierCache]);

  // Form field change handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'primaryNumber') {
      const formattedNumber = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedNumber
      }));
    } else if (name === 'date_of_birth') {
      const age = calculateAge(value);
      if (age < 18) {
        setErrors(prev => ({
          ...prev,
          [name]: 'You must be at least 18 years old to create a circle. We recommend joining an existing circle instead.'
        }));
      } else if (!isValidDate(value, 'birth_date')) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Please enter a valid date'
        }));
      } else {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === 'monthly_payment_date') {
      if (!isValidDate(value)) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Please select a valid date (not in the future)'
        }));
      } else {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (name === 'monthly_payment_per_line') {
      // Only allow numbers and decimal point
      const numericValue = value.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const decimalCount = (numericValue.match(/\./g) || []).length;
      if (decimalCount <= 1) {
        setFormData(prev => ({
          ...prev,
          [name]: numericValue
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    setErrors(prev => ({ ...prev, [name]: '' }));
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };
  
  // Special handler for carrier selection
  const handleCarrierChange = (e) => {
    const carrierSlug = e.target.value;
    
    // Set the carrier in formData
    setFormData(prev => ({
      ...prev,
      carrier: carrierSlug,
      // Reset plan name when carrier changes
      planName: ''
    }));
    
    // Set touched field
    setTouchedFields(prev => ({
      ...prev,
      carrier: true
    }));
    
    // Clear any errors
    setErrors(prev => ({
      ...prev,
      carrier: ''
    }));
    
    // Force update the availablePlans directly from cache
    if (carrierCache[carrierSlug]) {
      setAvailablePlans([...carrierCache[carrierSlug]]);
    } else {
      setAvailablePlans([]);
    }
  };

  // Checkbox change handler
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'agreeCircledin') {
      setAgreeCircledin(checked);
    } else if (name === 'agreeCarrier') {
      setAgreeCarrier(checked);
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  // Validate step
  const validateStep = (step) => {
    let stepErrors = {};
    switch (step) {
      case 0:
        if (!formData.carrier) stepErrors.carrier = 'Carrier is required';
        if (!formData.planName) stepErrors.planName = 'Plan name is required';
        if (!formData.customerDuration) stepErrors.customerDuration = 'Customer duration is required';
        if (!formData.primaryNumber) {
          stepErrors.primaryNumber = 'Primary number is required';
        } else {
          const digitsOnly = formData.primaryNumber.replace(/\D/g, '');
          if (digitsOnly.length !== 10) {
            stepErrors.primaryNumber = 'Please enter a valid 10-digit phone number';
          }
        }
        break;
      case 1:
        if (!formData.circleName) stepErrors.circleName = 'Circle name is required';
        if (!formData.familySize) {
          stepErrors.familySize = 'Family size is required';
        } else if (parseInt(formData.familySize) < 1 || parseInt(formData.familySize) > 10) {
          stepErrors.familySize = 'Family size must be between 1 and 10';
        }

        if (!formData.monthly_payment_date) {
          stepErrors.monthly_payment_date = 'Monthly payment date is required';
        } else if (!isValidDate(formData.monthly_payment_date)) {
          stepErrors.monthly_payment_date = 'Please select a valid date (not in the future)';
        }

        if (!formData.monthly_payment_per_line) {
          stepErrors.monthly_payment_per_line = 'Monthly payment per line is required';
        } else if (isNaN(parseFloat(formData.monthly_payment_per_line)) || parseFloat(formData.monthly_payment_per_line) < 0) {
          stepErrors.monthly_payment_per_line = 'Please enter a valid amount';
        }

        if (!formData.date_of_birth) {
          stepErrors.date_of_birth = 'Date of birth is required';
        } else {
          const age = calculateAge(formData.date_of_birth);
          if (age < 18) {
            stepErrors.date_of_birth = 'You must be at least 18 years old to create a circle. We recommend joining an existing circle instead.';
          } else if (!isValidDate(formData.date_of_birth, 'birth_date')) {
            stepErrors.date_of_birth = 'Please enter a valid date';
          }
        }

        if (!agreeCircledin) stepErrors.agreeCircledin = 'You must agree to Circledin Terms of Service';
        if (!agreeCarrier) stepErrors.agreeCarrier = 'You must agree to carrier Terms of Service';
        break;
      default:
        break;
    }
    return stepErrors;
  };

  // Navigation handlers - simplified to avoid circular dependencies
  const handleNext = () => {
    const stepErrors = validateStep(activeStep);
    if (Object.keys(stepErrors).length === 0) {
      setActiveStep(activeStep + 1);
      setErrors({});
      setError(null);
      scrollToTop();
    } else {
      // Set errors
      setErrors(stepErrors);
      
      // Mark all fields with errors as touched to display error messages
      const touchedErrorFields = {};
      Object.keys(stepErrors).forEach(field => {
        touchedErrorFields[field] = true;
      });
      setTouchedFields(prev => ({ ...prev, ...touchedErrorFields }));
      
      // Set global error
      setError('Please fill out all required fields before continuing.');
      
      // Scroll to the top to show the error message
      scrollToTop();
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      // Navigate to previous page/home when on first step
      navigate('/');
    } else {
      // Go to previous step when not on first step
      setActiveStep(activeStep - 1);
      scrollToTop();
    }
  };

  const handleInfoOpen = () => setOpenInfoDialog(true);
  const handleInfoClose = () => setOpenInfoDialog(false);

  // Create circle handler
  const handleCreateCircle = async () => {
    const stepErrors = validateStep(1);
    if (Object.keys(stepErrors).length === 0) {
      setIsLoading(true);
      try {
        const token = await getAccessTokenSilently();

        // Get the selected plan
        const selectedPlan = availablePlans.find(plan => plan.id === parseInt(formData.planName));

        // Find carrier by name
        const carrier = carriers.find(c => c.name === selectedPlan?.carrier_name);

        if (!carrier) {
          throw new Error('Selected carrier not found');
        }

        const circleData = {
          name: formData.circleName,
          carrier: carrier.id,
          carrier_plan: parseInt(formData.planName),
          family_size: parseInt(formData.familySize, 10),
          timeline: formData.customerDuration,
          account_phone_number: formData.primaryNumber.replace(/\D/g, ''),
          is_public: true,
          monthly_payment_date: formatDateForBackend(formData.monthly_payment_date),
          monthly_payment_per_line: parseFloat(formData.monthly_payment_per_line).toFixed(2),
          date_of_birth: formatDateForBackend(formData.date_of_birth),
          plan_type: 'consumer'
        };

        // Use circleService instead of direct fetch
        const response = await circleService.createCircle(circleData, token);
        
        setActiveStep(2);
        scrollToTop();
      } catch (error) {
        console.error('Error creating circle:', error);
        // Check for different types of error information
        const errorMessage = 
          error.friendlyMessage || 
          error.message || 
          (error.response?.data ? JSON.stringify(error.response.data) : 'Failed to create circle. Please try again.');
        
        setError(errorMessage);
        
        // Scroll to top to show error
        scrollToTop();
      } finally {
        setIsLoading(false);
      }
    } else {
      // Set errors
      setErrors(stepErrors);
      
      // Mark all fields with errors as touched to display error messages
      const touchedErrorFields = {};
      Object.keys(stepErrors).forEach(field => {
        touchedErrorFields[field] = true;
      });
      setTouchedFields(prev => ({ ...prev, ...touchedErrorFields }));
      
      // Set global error
      setError('Please fill out all required fields before creating your circle.');
      
      // Scroll to the top to show the error message
      scrollToTop();
    }
  };

  // Steps definition
  const steps = [
    { label: 'Carrier Information', icon: Phone },
    { label: 'Circle Details', icon: Users },
    { label: 'Confirm', icon: Check },
  ];

  // Progress bar animation
  const progressSpring = useSpring({
    width: `${(activeStep / (steps.length - 1)) * 100}%`,
    config: { tension: 300, friction: 20 },
  });

  return (
    <div className="modern-post-circle-wrapper">
      {/* Background Elements */}
      <div className="organic-blobs">
        <div className="organic-blob blob-1"></div>
        <div className="organic-blob blob-2"></div>
        <div className="organic-blob blob-3"></div>
      </div>

      <div className="enhanced-particles">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`enhanced-particle particle-${i % 5}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 10 + 8}s`
            }}
          />
        ))}
      </div>

      <div className="gradient-overlay gradient-1"></div>
      <div className="gradient-overlay gradient-2"></div>

      <div className="modern-post-circle-container">
        {/* Title */}
        <div className="title-container">
          <h1 className="modern-post-circle-title">Create Your Circle</h1>
        </div>

        {/* Progress Bar */}
        <div className="modern-progress-container">
          <animated.div
            className="modern-progress-bar"
            style={progressSpring}
          />
        </div>

        {/* Stepper */}
        <div className="modern-steps-container">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            return (
              <div
                key={index}
                className={`modern-step ${index <= activeStep ? 'active' : ''}`}
              >
                <motion.div
                  className="modern-step-icon-container"
                  initial={{ scale: 0.9, opacity: 0.6 }}
                  animate={{
                    scale: index <= activeStep ? 1 : 0.9,
                    opacity: index <= activeStep ? 1 : 0.6
                  }}
                  transition={{ duration: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <StepIcon size={20} />
                  <div className="step-glow"></div>
                </motion.div>
                <motion.span
                  className="modern-step-label"
                  initial={{ opacity: 0.6 }}
                  animate={{ opacity: index <= activeStep ? 1 : 0.6 }}
                  transition={{ duration: 0.4 }}
                >
                  {step.label}
                </motion.span>
                {index < steps.length - 1 && (
                  <div className={`step-connector ${index < activeStep ? 'active' : ''}`}></div>
                )}
              </div>
            );
          })}
        </div>

        {/* Main Form Container with Glassmorphism */}
        <motion.div
          className="modern-form-card"
          onMouseMove={handleMouseMove}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            transform: `perspective(1000px) rotateX(${mousePosition.y * 2}deg) rotateY(${mousePosition.x * 2}deg)`
          }}
        >
          
          <div className="card-noise"></div>

          {/* Form Content with AnimatePresence for Smooth Transitions */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
              className="form-content"
            >
              {activeStep === 0 && (
                <div className="carrier-info-step">
                  <h2>Select Your Carrier</h2>
                  
                  {error && (
                    <div className="error-banner">
                      <AlertTriangle size={20} />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="modern-form-group">
                    <label>Carrier</label>
                    <div className="custom-select-wrapper">
                      {isLoadingCarriers ? (
                        <SkeletonDropdown />
                      ) : (
                        <Select
                          className="custom-select"
                          name="carrier"
                          value={formData.carrier}
                          onChange={handleCarrierChange}
                          displayEmpty
                          renderValue={formData.carrier ? undefined : () => "Select a carrier"}
                          MenuProps={{
                            className: "custom-menu",
                            PaperProps: {
                              style: {
                                backgroundColor: 'rgba(0, 30, 36, 0.95)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                color: 'white',
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)'
                              }
                            }
                          }}
                        >
                          {carriers.map((carrier) => (
                            <MenuItem key={carrier.slug} value={carrier.slug}>
                              {carrier.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                      {errors.carrier && touchedFields.carrier && (
                        <ErrorMessage message={errors.carrier} />
                      )}
                    </div>
                  </div>

                  <div className="modern-form-group">
                    <label>Plan Name</label>
                    <div className="custom-select-wrapper">
                      {!formData.carrier || isLoadingPlans ? (
                        <SkeletonDropdown />
                      ) : (
                        <Select
                          className="custom-select"
                          name="planName"
                          value={formData.planName}
                          onChange={handleChange}
                          disabled={!formData.carrier || availablePlans.length === 0}
                          displayEmpty
                          renderValue={formData.planName ? undefined : () => 
                            availablePlans.length === 0 
                              ? "No plans available for this carrier" 
                              : "Select a plan"
                          }
                          MenuProps={{
                            className: "custom-menu",
                            PaperProps: {
                              style: {
                                backgroundColor: 'rgba(0, 30, 36, 0.95)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                color: 'white',
                                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)'
                              }
                            }
                          }}
                        >
                          {availablePlans.length === 0 ? (
                            <MenuItem disabled>No plans available</MenuItem>
                          ) : (
                            availablePlans.map((plan) => (
                              <MenuItem key={plan.id} value={plan.id}>
                                {plan.name} - ${plan.per_line_cost || 'N/A'}/line
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      )}
                      {errors.planName && touchedFields.planName && (
                        <ErrorMessage message={errors.planName} />
                      )}
                    </div>
                  </div>

                  <div className="modern-form-group">
                    <label>How long have you been a customer?</label>
                    <div className="custom-select-wrapper">
                      <Select
                        className="custom-select"
                        name="customerDuration"
                        value={formData.customerDuration}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={formData.customerDuration ? undefined : () => "Select duration"}
                        MenuProps={{
                          className: "custom-menu",
                          PaperProps: {
                            style: {
                              backgroundColor: 'rgba(0, 30, 36, 0.95)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              color: 'white',
                              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)'
                            }
                          }
                        }}
                      >
                        {customerDurations.map((duration) => (
                          <MenuItem key={duration} value={duration}>
                            {duration}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.customerDuration && touchedFields.customerDuration && (
                        <ErrorMessage message={errors.customerDuration} />
                      )}
                    </div>
                  </div>

                  <div className="modern-form-group">
                    <div className="label-with-tooltip">
                      <label>Primary Number Associated with this Account</label>
                      <Tooltip
                        title="This is typically the first number listed on your bill."
                        placement="top"
                        classes={{ tooltip: 'custom-tooltip' }}
                      >
                        <span className="tooltip-icon-wrapper">
                          <HelpCircle size={16} />
                        </span>
                      </Tooltip>
                    </div>
                    <div className="custom-input-wrapper">
                      <TextField
                        id="primary-number-field"
                        name="primaryNumber"
                        value={formData.primaryNumber}
                        onChange={handleChange}
                        placeholder="(123) 456-7890"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          maxLength: 14,
                          style: { color: 'white' }
                        }}
                        error={!!errors.primaryNumber && touchedFields.primaryNumber}
                      />
                      {errors.primaryNumber && touchedFields.primaryNumber && (
                        <ErrorMessage message={errors.primaryNumber} />
                      )}
                    </div>
                  </div>

                  {/* Remove any global error that might be showing */}
                  {error && !errors.carrier && !errors.planName && !errors.customerDuration && !errors.primaryNumber && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="error-message global-error"
                      style={{ marginBottom: '20px' }}
                    >
                      {error}
                    </motion.div>
                  )}

                  <div className="benefits-section">
                    <BenefitItem icon={Shield} text="Protected Information" />
                    <BenefitItem icon={CreditCard} text="No Credit Check" />
                    <BenefitItem icon={Clock} text="Quick Setup" />
                  </div>
                </div>
              )}

              {activeStep === 1 && (
                <div className="circle-details-step">
                  <h2>Circle Details</h2>
                  
                  {error && (
                    <div className="error-banner">
                      <AlertTriangle size={20} />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="modern-form-group">
                    <label>Give your Circle a Name</label>
                    <div className="custom-input-wrapper">
                      <TextField
                        name="circleName"
                        value={formData.circleName}
                        onChange={handleChange}
                        placeholder="My Awesome Circle"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          style: { color: 'white' }
                        }}
                        error={!!errors.circleName && touchedFields.circleName}
                      />
                      {errors.circleName && touchedFields.circleName && (
                        <ErrorMessage message={errors.circleName} />
                      )}
                    </div>
                  </div>

                  <div className="modern-form-group">
                    <label>Current Family Size</label>
                    <div className="custom-input-wrapper">
                      <TextField
                        name="familySize"
                        value={formData.familySize}
                        onChange={handleChange}
                        type="number"
                        placeholder="4"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          min: 1,
                          max: 10,
                          style: { color: 'white' }
                        }}
                        error={!!errors.familySize && touchedFields.familySize}
                      />
                      {errors.familySize && touchedFields.familySize && (
                        <ErrorMessage message={errors.familySize} />
                      )}
                    </div>
                  </div>

                  <div className="modern-form-group">
                    <label>Monthly Payment Date</label>
                    <div className="custom-input-wrapper">
                      <TextField
                        name="monthly_payment_date"
                        value={formData.monthly_payment_date}
                        onChange={handleChange}
                        type="date"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          max: new Date().toISOString().split('T')[0],
                          style: { 
                            color: 'white',
                            colorScheme: 'dark' // Ensures the date picker has dark styling
                          }
                        }}
                        error={!!errors.monthly_payment_date && touchedFields.monthly_payment_date}
                      />
                      {errors.monthly_payment_date && touchedFields.monthly_payment_date && (
                        <ErrorMessage message={errors.monthly_payment_date} />
                      )}
                    </div>
                  </div>

                  <div className="modern-form-group">
                    <label>Monthly Payment per Line</label>
                    <div className="price-input-container custom-input-wrapper">
                      <TextField
                        className="price-input"
                        name="monthly_payment_per_line"
                        value={formData.monthly_payment_per_line}
                        onChange={handleChange}
                        type="number"
                        placeholder="45.00"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          startAdornment: <span className="price-symbol">$</span>
                        }}
                        inputProps={{
                          step: "0.01",
                          min: 0,
                          style: { 
                            color: 'white', 
                            paddingLeft: '28px' 
                          }
                        }}
                        error={!!errors.monthly_payment_per_line && touchedFields.monthly_payment_per_line}
                      />
                      {errors.monthly_payment_per_line && touchedFields.monthly_payment_per_line && (
                        <ErrorMessage message={errors.monthly_payment_per_line} />
                      )}
                    </div>
                  </div>

                  <div className="modern-form-group">
                    <label>Date of Birth</label>
                    <div className="custom-input-wrapper">
                      <TextField
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        type="date"
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          max: new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                            .toISOString()
                            .split('T')[0],
                          min: "1900-01-01",
                          style: { 
                            color: 'white',
                            colorScheme: 'dark' // Ensures the date picker has dark styling
                          }
                        }}
                        error={!!errors.date_of_birth && touchedFields.date_of_birth}
                      />
                      {(errors.date_of_birth && touchedFields.date_of_birth) ? (
                        <ErrorMessage message={errors.date_of_birth} />
                      ) : (
                        <span className="helper-text">You must be at least 18 years old to create a circle</span>
                      )}
                    </div>
                  </div>

                  <div className="custom-checkbox-group">
                    <label className="custom-checkbox-container">
                      <input
                        type="checkbox"
                        name="agreeCircledin"
                        checked={agreeCircledin}
                        onChange={handleCheckboxChange}
                      />
                      <span className={`custom-checkmark ${errors.agreeCircledin && touchedFields.agreeCircledin ? 'error-checkbox' : ''}`}></span>
                      <span className="checkbox-label">
                        I agree to the <Link href="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">Terms of Service</Link> and to make Circledin an Account Manager to manage my account.
                      </span>
                    </label>
                    {errors.agreeCircledin && touchedFields.agreeCircledin && (
                      <ErrorMessage message={errors.agreeCircledin} />
                    )}
                  </div>

                  <div className="custom-checkbox-group">
                    <label className="custom-checkbox-container">
                      <input
                        type="checkbox"
                        name="agreeCarrier"
                        checked={agreeCarrier}
                        onChange={handleCheckboxChange}
                      />
                      <span className={`custom-checkmark ${errors.agreeCarrier && touchedFields.agreeCarrier ? 'error-checkbox' : ''}`}></span>
                      <span className="checkbox-label">
                        I agree to {formData.carrier}'s{' '}
                        <Link href={`/${formData.carrier}-terms`} target="_blank" rel="noopener noreferrer" className="terms-link">
                          Terms of Service
                        </Link>
                      </span>
                    </label>
                    {errors.agreeCarrier && touchedFields.agreeCarrier && (
                      <ErrorMessage message={errors.agreeCarrier} />
                    )}
                  </div>

                  {/* Show overall error message for this section if needed */}
                  {error && Object.keys(errors).length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="error-message global-error"
                    >
                      <AlertTriangle size={16} />
                      <span>Please fill out all required fields marked with errors above.</span>
                    </motion.div>
                  )}

                  <motion.button
                    className="info-button"
                    onClick={handleInfoOpen}
                    whileHover={{
                      scale: 1.05,
                      boxShadow: "0 10px 25px rgba(255, 71, 117, 0.35)"
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Info size={16} />
                    Why Become an Account Owner?
                  </motion.button>
                </div>
              )}

              {activeStep === 2 && (
                <ConfirmCircle
                  formData={formData}
                  onEdit={() => { setActiveStep(0); scrollToTop(); }}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          {activeStep < 2 && (
            <div className="form-actions">
              <motion.button
                className="back-button"
                onClick={handleBack}
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 10px 25px rgba(63, 81, 181, 0.25)"
                }}
                whileTap={{ scale: 0.98 }}
              >
                <ChevronLeft size={18} />
                Back
              </motion.button>

              <motion.button
                className="next-button"
                onClick={activeStep === 1 ? handleCreateCircle : handleNext}
                disabled={isLoading}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 10px 25px rgba(255, 71, 117, 0.35)"
                }}
                whileTap={{ scale: 0.98 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <>
                    {activeStep === 1 ? 'Create My Circle' : 'Next'}
                    {activeStep === 1 ? <Check size={18} /> : <ChevronRight size={18} />}
                  </>
                )}
              </motion.button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Info Dialog */}
      <Dialog
        open={openInfoDialog}
        onClose={handleInfoClose}
        PaperProps={{
          className: "custom-dialog-paper"
        }}
      >
        <DialogTitle className="custom-dialog-title">
          Why Become an Account Owner?
        </DialogTitle>
        <DialogContent className="custom-dialog-content">
          <Typography variant="body1">
            As an Account Owner, you can:
          </Typography>
          <ul>
            <li>Save money on your mobile plan by sharing costs</li>
            <li>Help others access affordable mobile services</li>
            <li>Manage your circle and customize settings</li>
            <li>Earn rewards and bonuses for successful circles</li>
          </ul>
        </DialogContent>
        <DialogActions className="custom-dialog-actions">
          <Button
            onClick={handleInfoClose}
            className="dialog-close-button"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PostCircle;